import { Node, Slice, Fragment } from '@tiptap/pm/model';
import { Plugin, Transaction } from '@tiptap/pm/state';
import { EditorView } from '@tiptap/pm/view';
import type { ImageUploaderPluginOptions } from '../customExtensions/ImageUploadExtension';
import constants from '@/utilities/constants';
import { mediaType, InlineMediaNode } from '@superthread-com/extension-inline-media/src/index';
import { Editor } from '@tiptap/core';
import { isInTable, selectionCell } from '@tiptap/pm/tables';
import { getRootStyle } from '@/utilities/rootStyles';

const fileCache: { [key: string]: File | string } = {};

// 8px on both sides
const tableCellPadding = 16;

export function imageUploader(options: ImageUploaderPluginOptions) {
  const plugin = new ImageUploaderPlugin(options);
  const dummy = {};

  return new Plugin({
    props: {
      handleDOMEvents: {
        keydown(view) {
          return !plugin?.setView(view);
        },

        drop(view, event: DragEvent) {
          if (checkIfDropElementToIsTableCell(event)) {
            plugin.dropIntoTableCell = true;
            plugin.tableCellWidth = (event.target as HTMLElement)?.offsetWidth - tableCellPadding;
          }
          return !plugin?.setView(view);
        },

        focus(view) {
          return !plugin?.setView(view);
        },
      },

      handlePaste(view, event, slice) {
        return plugin?.setView(view).handlePaste(event, slice) || false;
      },

      handleDrop(view, event) {
        return plugin?.setView(view).handleDrop(event) || false;
      },

      transformPastedHTML(html) {
        if (plugin?.pastedFileFromClipboard) {
          // !IMPORTANT: clear the pastedFileFromClipboard flag
          plugin.pastedFileFromClipboard = false;
          return '';
        }
        const replacedHtml = html.replaceAll('"', "'");
        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(replacedHtml, 'text/html');

        const imgs = Array.from(parsedHtml.getElementsByTagName('img'));
        for (const img of imgs) {
          img.remove();
        }

        const body = parsedHtml?.body?.innerHTML;
        return typeof body === 'string' ? body : html;
      },

      transformPasted(slice, view) {
        return plugin?.setView(view).transformPasted(slice) || slice;
      },
    },

    state: {
      init() {
        return dummy;
      },

      apply(tr, _value, _oldState, newState) {
        const filesOrUrls = tr.getMeta('uploadImages');

        if (filesOrUrls) {
          const arr: Array<File | string> =
            typeof filesOrUrls === 'string' || filesOrUrls instanceof File
              ? [filesOrUrls]
              : Array.from(filesOrUrls); /// Probably a FileList or an array of files/urls

          // give some time for editor, otherwise history plugin forgets history
          setTimeout(() => {
            arr.forEach((item, i) => plugin?.beforeUpload(item, newState.selection.from + i));
            tr.setMeta('uploadImages', undefined);
          }, 10);
        }

        return dummy;
      },
    },
  });
}

export class ImageUploaderPlugin {
  public view!: EditorView;

  public pastedFileFromClipboard: boolean = false;
  public dropIntoTableCell: boolean = false;
  public tableCellWidth: number | null = null;

  constructor(public config: ImageUploaderPluginOptions) {}

  private hasCordinatesAndFiles(coordinates: { pos: number } | null, filesLength: number) {
    return coordinates && coordinates.pos > -1 && filesLength;
  }

  private getImageHeight(image: HTMLImageElement, width: number) {
    return width !== image.width ? calculateHeight(image.width, image.height, width) : image.height;
  }

  private isFragmentPlaceholder(fragment: Fragment) {
    return (
      fragment &&
      // @ts-ignore
      fragment?.content[0]?.type.name === constants.stImagePlaceholder &&
      // @ts-ignore
      fragment?.content[0]?.type.name === InlineMediaNode.GenericPlaceholder
    );
  }

  public handleDrop(event: DragEvent) {
    if (!event.dataTransfer?.files.length) return;

    const coordinates = this.view.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    });
    const files = Array.from(event.dataTransfer.files);
    if (!this.hasCordinatesAndFiles(coordinates, files.length)) return;
    if (this.config.preventInsertFiles) {
      event.preventDefault();
      this.config.emitter.$emit('preventInsertFiles', this.config.editorId);
      return false;
    }

    Promise.any(
      files.map(async (element, index) => {
        const image = this.config.acceptMimes.includes(element.type);
        if (image) {
          this.pastedFileFromClipboard = true;
          const file: File | null = element;

          const imgSrc = await readImage(file);

          const img = new Image();

          img.onload = () => {
            const width = checkWidth(this.config.editor.id, img.width);
            const height = this.getImageHeight(img, width);

            this.beforeUpload(imgSrc as any, (coordinates?.pos ?? 0) + index, file?.name, {
              width,
              height,
            });
          };

          img.src = imgSrc as any;
        } else {
          const file = element;
          if (!file) return;

          this.beforeUploadFile(file, (coordinates?.pos ?? 0) + index, file?.name);
        }
      })
    ).catch((e) => {
      console.warn(e);
    });

    return true;
  }

  public handlePaste(event: ClipboardEvent, slice: Slice) {
    const { content: fragment } = slice;

    if (this.isFragmentPlaceholder(fragment)) {
      return false;
    }

    const items = Array.from(event.clipboardData?.items || []);
    if (!items.length) return true;
    if (this.config.preventInsertFiles) {
      this.config.emitter.$emit('preventInsertFiles', this.config.editorId);
      return false;
    }
    Promise.any(
      items.map(async (element) => {
        const image = this.config.acceptMimes.includes(element.type);
        if (image) {
          this.pastedFileFromClipboard = true;
          const file: File | null = element.getAsFile();

          const imgSrc = await readImage(file);

          const img = new Image();

          img.onload = () => {
            const width = checkWidth(this.config.editor.id, img.width);
            const height = this.getImageHeight(img, width);

            this.beforeUpload(imgSrc as any, this.view.state.selection.from, file?.name, {
              width,
              height,
            });
          };

          img.src = imgSrc as any;
        } else {
          const file = element.getAsFile();
          if (!file) return;

          this.beforeUploadFile(file, this.view.state.selection.from, file?.name);
        }
      })
    ).catch((e) => {
      console.warn(e);
    });
  }

  public transformPasted(slice: Slice) {
    const imageNodes: Array<{ url: string; id: string }> = [];
    const children: Node[] = [];
    slice.content.forEach((child) => {
      let newChild = child;

      /// if the node itself is image
      if (child.type.name === constants.stImagePlaceholder) {
        imageNodes.push({
          id: newChild.attrs.uploadId,
          url: child.attrs.imgsrc || child.attrs['data-src'],
        });
      }

      if (child.type.name === constants.stImage) {
        // check if is in table cell
        // this will check if current seleciton is in table cell or if drop was in table cell
        const tableCell = this.dropIntoTableCell
          ? this.tableCellWidth
          : checkIfInsertingIntoTableCell(this.config.editor);
        let imageSmallerThanTableCell = false;
        if (tableCell && tableCell >= child.attrs.width) {
          imageSmallerThanTableCell = true;
        }
        if (tableCell) {
          const height = calculateHeight(child.attrs.width, child.attrs.height, tableCell);
          const width = imageSmallerThanTableCell ? child.attrs.width : tableCell;
          newChild = child.type.create({
            ...child.attrs,
            style: `width: ${width}px`,
            width,
            height: imageSmallerThanTableCell ? child.attrs.height : height,
          });
        }
      }
      children.push(newChild);
    });

    imageNodes.forEach(({ url, id }) => {
      this.uploadImageForId(url, id, 'slika');
    });

    // reset the dropIntoTableCell flags
    this.tableCellWidth = null;
    this.dropIntoTableCell = false;

    return new Slice(Fragment.fromArray(children), slice.openStart, slice.openEnd);
  }

  /**
   * This method is to be used with generic media files.
   * Images are handled separately by `beforeUpload`.
   */
  public beforeUploadFile(file: File, at: number, fileName = '') {
    const tr = this.view.state.tr;
    if (!tr.selection.empty) {
      tr.deleteSelection();
    }

    if (at < 0) {
      at = this.view.state.selection.from;
    }

    const node = this.view.state.schema.nodes[InlineMediaNode.GenericPlaceholder].create({
      uploadId: this.config.id(),
      fileName: fileName,
      fileType: file.type,
      mediaType: mediaType(file.type),
      uploadProgress: 0,
    });

    // preventUpdate meta will prevent editor to emit update message - no need to update doc with base64 image
    tr.replaceWith(at, at, node).setMeta('preventUpdate', true);
    this.view.dispatch(tr);

    /// upload image for above node
    this.uploadFileForId(file, node.attrs.uploadId, fileName);
  }

  /**
   * This method is to be used with generic media files.
   * Images are handled separately by `uploadImageForId`.
   */
  public async uploadFileForId(file: File, id: string, fileName: string) {
    const fileObject = file ? await this.requestUpload(file, `editor-${id}`) : null;

    const placeholderNodes = this.getNodePositions(InlineMediaNode.GenericPlaceholder, id);
    if (!placeholderNodes.length) {
      return;
    }

    const tr = this.view.state.tr.setMeta('addToHistory', false).setMeta('imageInsert', true);

    if (fileObject) {
      this.replaceNodesAtPositions(tr, placeholderNodes, (node) => {
        return this.view.state.schema.nodes[InlineMediaNode.Generic].create({
          ...node.attrs,
          url: (fileObject as any)?.url || '',
          fileName: fileName,
          fileType: file.type,
          mediaType: mediaType(file.type),
        });
      });
    } else {
      this.deleteNodesAtPositions(tr, placeholderNodes);
      this.config.emitter?.$emit('fileInsertError', {
        editorId: this.config.editorId,
        uploadId: id,
      });
    }

    this.view.dispatch(tr);
    fileCache[id] = '';
  }

  public async beforeUpload(
    fileOrUrl: File | string,
    at: number,
    fileName: string = '',
    { width, height }: { width: number; height: number } = {
      width: 0,
      height: 0,
    }
  ) {
    const tr = this.view.state.tr;
    if (!tr.selection.empty) {
      tr.deleteSelection();
    }

    if (at < 0) {
      at = this.view.state.selection.from;
    }

    let tempImage;
    if (!width || !height) {
      tempImage = new Image();
      tempImage.src = fileOrUrl as string;
    }

    /// insert image view node - uploading state.
    const node = await this.newUploadingImageNode({
      src: fileOrUrl,
      width: width || tempImage?.width,
      height: height || tempImage?.height,
    });
    // preventUpdate meta will prevent editor to emit update message - no need to update doc with base64 image
    tr.replaceWith(at, at, node).setMeta('preventUpdate', true);
    this.view.dispatch(tr);

    /// upload image for above node
    this.uploadImageForId(fileOrUrl, node.attrs.uploadId, fileName);
  }

  public async newUploadingImageNode(attrs?: any): Promise<Node> {
    const uploadId = this.config.id();
    fileCache[uploadId] = attrs.src || attrs['data-src'];

    const { width, height } = readImageAndCheckDimensions(attrs, this.config.editor);
    return this.view.state.schema.nodes[constants.stImagePlaceholder].create({
      ...attrs,
      imgsrc: '',
      uploadId,
      width,
      height,
    });
  }

  /**
   * @param nodeName The node that is being searched
   * @param uploadId The upload id of the placeholder - it is assigned
   * when the upload is initiated and the placeholder is created.
   * @returns
   */
  getNodePositions(nodeName: string, uploadId: string) {
    const positions: Array<{ node: Node; pos: number }> = [];
    this.view.state.doc.descendants((node, pos) => {
      if (node.type.name === nodeName && node.attrs.uploadId === uploadId) {
        positions.push({ node, pos });
      }
    });

    return positions;
  }

  /**
   * This replaces the given nodes. Dispatching the transaction is still required.
   * @param tr
   * @param nodes
   * @param replacement
   */
  replaceNodesAtPositions(
    tr: Transaction,
    nodes: { node: Node; pos: number }[],
    replacement: Node | ((node: Node) => Node)
  ) {
    nodes.forEach(({ node, pos }) => {
      const newNode = typeof replacement === 'function' ? replacement(node) : replacement;
      tr.replaceWith(pos, pos + 1, newNode);
    });
  }

  /**
   * This deletes the given nodes. Dispatching the transaction is still required.
   * @param tr
   * @param nodes
   */
  deleteNodesAtPositions(tr: Transaction, nodes: { node?: Node; pos: number }[]) {
    nodes.forEach(({ pos }) => {
      tr.delete(pos, pos + 1);
    });
  }

  async requestUpload(file: File, id: string) {
    try {
      return await this.config.upload(
        this.config.editor.id,
        file,
        this.config.resourceType,
        this.config.parentResourceType,
        this.config.parentResourceId,
        id
      );
    } catch (err: any) {
      if (err.message === 'pageCreated') {
        try {
          return await this.config.upload(
            this.config.editor.id,
            file || '',
            this.config.resourceType,
            this.config.parentResourceType,
            this.config.parentResourceId,
            id
          );
        } catch {
          return null;
        }
      }
      return null;
    }
  }

  public async uploadImageForId(fileOrUrl: File | string, id: string, fileName: string) {
    let file: string | File | null = fileOrUrl;
    if (typeof file === 'string') {
      file = await webImg2File(file, fileName);
    }

    const fileObject = file ? await this.requestUpload(file, `editor-${id}`) : null;

    const imageNodes = this.getNodePositions(constants.stImagePlaceholder, id);
    if (!imageNodes.length) {
      return;
    }

    /// disallow user from undoing back to 'uploading' state.
    const tr = this.view.state.tr.setMeta('addToHistory', false).setMeta('imageInsert', true);
    const name = getFileName(fileName);

    if (fileObject) {
      this.replaceNodesAtPositions(tr, imageNodes, (node) => {
        return this.view.state.schema.nodes[constants.stImage].create({
          ...node.attrs,
          width: node.attrs?.width,
          imgsrc: (fileObject as any)?.url || '',
          'blur-hash': (fileObject as any)?.blurhash,
          style: `width: ${node.attrs?.width}px`,
          alt: name,
        });
      });
    } else {
      this.deleteNodesAtPositions(tr, imageNodes);
      this.config.emitter?.$emit('fileInsertError', this.config.editorId);
    }

    this.view.dispatch(tr);
    fileCache[id] = '';
  }

  public setView(view: EditorView): this {
    this.view = view;
    return this;
  }
}

const webImg2File = (imgUrl: string, fileName: string): Promise<File | null> => {
  function imgToBase64(url: string): Promise<string> {
    if (url.startsWith('data:image/')) {
      // If it's already a base64 encoded image, no need to convert
      return Promise.resolve(url);
    }
    let canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.crossOrigin = 'Anonymous';
    img.setAttribute('referrerpolicy', 'no-referrer');
    img.src = url;
    return new Promise((resolve, reject) => {
      img.onload = function () {
        canvas.height = img.height;
        canvas.width = img.width;
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
        // @ts-ignore
        canvas = null;
      };
      img.onerror = reject;
    });
  }

  const base64toFile = (base: string, filename: string): File => {
    const arr = base.split(',');
    // @ts-ignore
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], `${filename}`, { type: mime });
  };

  return imgToBase64(imgUrl)
    .then((base) => {
      return base64toFile(base, fileName);
    })
    .catch(() => {
      return null;
    });
};

const readImage = (image: any) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      resolve(e.target?.result);
    };
    reader.readAsDataURL(image);
  });
};

const getFileName = (name: string) => {
  const nameArr = name.split('.');
  nameArr.splice(nameArr.length - 1, 1);
  return nameArr.join('');
};

export const checkWidth = (editorId: any, imageWidth: number): number => {
  const p = document.querySelector(`#${editorId} p`);
  if (p) {
    let { width } = p.getBoundingClientRect();
    // this is needed for page editor
    if (editorId.includes('page')) {
      width -= parseInt(getRootStyle('--page-elements-side-padding')) * 2;
    }
    return imageWidth > width ? width : imageWidth;
  }
  return imageWidth;
};

export const calculateHeight = (width: number, height: number, preferredWidth: number) => {
  const ratio = height / width;
  return Math.round(ratio * preferredWidth);
};

export const getFileCache = (key: string) => {
  return fileCache[key];
};

const checkIfInsertingIntoTableCell = (editor: Editor) => {
  if (!isInTable(editor.view.state)) return null;

  const selectionCellNode = selectionCell(editor.view.state);
  if (selectionCellNode?.pos >= 0) {
    // get dom element of the cell
    const nodeDom = editor.view.nodeDOM(selectionCellNode.pos) as HTMLElement;
    return nodeDom?.offsetWidth - tableCellPadding || null;
  }
};

// this method loads image, reads width and height and checks if they if to editor
export const readImageAndCheckDimensions = (attrs: any, editor: Editor): any => {
  const tableCellWidth = checkIfInsertingIntoTableCell(editor);
  const imageIsSmallerThanTableCell = tableCellWidth && tableCellWidth >= attrs.width;

  const width = checkWidth(
    (editor as any).id,
    imageIsSmallerThanTableCell ? attrs.width : (tableCellWidth ?? attrs.width)
  );

  const height =
    width !== attrs.width ? calculateHeight(attrs.width, attrs.height, width) : attrs.height;

  return { src: attrs.src, width, height };
};

const checkIfDropElementToIsTableCell = (event: DragEvent) => {
  return (
    ['TD', 'TH'].includes((event.target as HTMLElement).nodeName) ||
    ((event.target as HTMLElement)?.parentNode &&
      ['TD', 'TH'].includes((event.target as HTMLElement)?.parentNode?.nodeName ?? ''))
  );
};
